import { openLocalDatabase, type RecentWorkspaceRecord } from "../persistence/local_database";

export interface RecentWorkspacePersistence {
  list(): Promise<readonly RecentWorkspaceRecord[]>;
  put(record: RecentWorkspaceRecord): Promise<void>;
}

/** Adapts the shared IndexedDB support database to the recent-workspace store. */
class IndexedDbRecentWorkspacePersistence implements RecentWorkspacePersistence {
  /** Lists stored handles without requesting filesystem permission. */
  public async list(): Promise<readonly RecentWorkspaceRecord[]> {
    const database = await openLocalDatabase();
    return database.getAll("workspaces");
  }

  /** Structured-clones one recent handle into browser-local storage. */
  public async put(record: RecentWorkspaceRecord): Promise<void> {
    const database = await openLocalDatabase();
    await database.put("workspaces", record);
  }
}

/** Stores recent handles only to support explicit workspace reconnection. */
export class RecentWorkspaceStore {
  private readonly persistence: RecentWorkspacePersistence;

  /** Uses IndexedDB in production while allowing deterministic contract injection. */
  public constructor(
    persistence: RecentWorkspacePersistence = new IndexedDbRecentWorkspacePersistence(),
  ) {
    this.persistence = persistence;
  }

  /** Returns the most recently opened handle, if one remains. */
  public async mostRecent(): Promise<RecentWorkspaceRecord | null> {
    const records = await this.persistence.list();
    return (
      [...records].sort((left, right) => right.lastOpenedAt.localeCompare(left.lastOpenedAt))[0] ??
      null
    );
  }

  /** Updates an equivalent handle or stores a new recent workspace. */
  public async remember(
    handle: FileSystemDirectoryHandle,
    openedAt: Date,
  ): Promise<RecentWorkspaceRecord> {
    const records = await this.persistence.list();
    let existing: RecentWorkspaceRecord | undefined;

    for (const record of records) {
      if (await record.handle.isSameEntry(handle)) {
        existing = record;
        break;
      }
    }

    const record: RecentWorkspaceRecord = {
      id: existing?.id ?? crypto.randomUUID(),
      name: handle.name,
      handle,
      lastOpenedAt: openedAt.toISOString(),
    };
    await this.persistence.put(record);
    return record;
  }
}
